/**
 * ehr#571 round 2 (GROUP_NOTE_10-20-V22 groupStats / rosterHTML, FAMILY_COUPLES_NOTE_10-20-V11): each
 * attendee's line in the roster ("Mood, +2 more", "Ready to sign", "Did not attend. Ready to sign",
 * "Pending review", "Co-signed"), worked out from the saved note with the same checks the note's
 * own "N left to sign" uses. Pure, so the Group and the Family & Couples pages agree.
 */
import { leftToSign, sessionEndOf } from './noteV81.js';
import { hoursAfterSession, isLateEntry, sessionEndAt } from './noteLate.js';
import { noteBlocks, withQuick, type QuickItemLike } from './noteLayouts.js';
import { groupSignChecks, type RosterGroup } from './groupNoteV26.js';
import { familySignChecks } from './familyNote.js';
import type { FamilyType, PartnerSafety } from '@ehr/shared';

export { ownSentenceMissing, sentences } from './groupNoteV26.js';

type Body = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const strs = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);

/** V22 26 B: a group member who did not attend has a no-show note (its own short layout). */
export const ABSENT_KIND = 'No-show / cancellation';
/** V22 ATTEND: a group says "Did not attend" for the stored "Not present". */
export const attendanceLabel = (a: string) => (a === 'Not present' ? 'Did not attend' : a);
/** The layout a note is written on: an absent group member's is the no-show one. */
export const layoutKindOf = (kind: string, absentMember: boolean) =>
  absentMember ? ABSENT_KIND : kind;

/** The text boxes of a saved note's body (what the member wrote), by key. */
export function bodyText(b: Body, keys: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of keys) if (typeof b[k] === 'string') out[k] = b[k] as string;
  return out;
}

export interface AttendeeContext {
  format: string;
  /** The note's kind: Group, Family or Couples. */
  kind: string;
  quick: readonly QuickItemLike[];
  /** Attendance is Not present (Did not attend on a group). */
  absent: boolean;
  group?: {
    members: readonly string[];
    summaryWritten: boolean;
    /** The Group summary and the other members' notes ("One sentence only about ..."). */
    shared: readonly string[];
    firstName: string;
  };
  /** ehr#571 round 2 (V11): a family or couples attendee. */
  family?: {
    type: FamilyType;
    billed: boolean;
    othersAway: boolean;
    summaryWritten: boolean;
    firstName: string;
  };
}

/** What a saved note still needs before it can be signed (labels only), in note order. */
export function attendeeLeft(b: Body, c: AttendeeContext): string[] {
  // a family member who did not come, too (V11); never the identified client
  const absentMember = c.absent && (!!c.group || (!!c.family && !c.family.billed));
  const kind = layoutKindOf(c.kind, absentMember);
  const blocks = withQuick(noteBlocks(c.format, kind, { absent: absentMember }), c.quick, c.format);
  const keys = blocks.filter((x) => x.kind === 'text').map((x) => x.id);
  // ehr#853: a Credify Note's own boxes (not layout blocks) are read for its check too
  if (c.format === 'CREDIFY') keys.push('objective', 'assessment');
  const text = bodyText(b, keys);
  const s = (b.session ?? {}) as { time?: unknown; end?: unknown; durationMin?: unknown };
  const out = leftToSign({
    blocks,
    kind,
    text,
    interventionsUsed: strs(b.interventionsUsed),
    necessity: str(b.necessity),
    safety: (b.safety as Parameters<typeof leftToSign>[0]['safety']) ?? null,
    diagnoses: Array.isArray(b.diagnoses) ? b.diagnoses.length : 0,
    start: str(s.time),
    end: sessionEndOf(s), // ehr#666: start plus length when no end was saved
    format: c.format, // ehr#853: a Credify Note's Objective and Assessment
  }).map((x) => x.label);
  if (c.family) {
    // the family's own items lead, as in the note (ClinicalNotesPanel signChecksHead)
    const lead = familySignChecks({
      type: c.family.type,
      present: !c.absent,
      partnerSafety: (b.partnerSafety as PartnerSafety | undefined) ?? null,
      billed: c.family.billed,
      othersAway: c.family.othersAway,
      firstName: c.family.firstName,
      summaryWritten: c.family.summaryWritten,
    }).map((x) => x.label);
    return [...new Set([...lead, ...out])];
  }
  if (!c.group) return out;
  // the group's own checks lead, as in the note's "N left to sign" (ClinicalNotesPanel signChecksHead)
  const lead = groupSignChecks({
    present: !c.absent,
    participation: str(b.participation),
    mood: str(b.groupMood),
    text,
    members: c.group.members,
    summaryWritten: c.group.summaryWritten,
    own: { firstName: c.group.firstName, shared: c.group.shared },
  }).map((x) => x.label);
  return [...new Set([...lead, ...out])];
}

export type RosterTone = 'ok' | 'warn' | 'done';
export interface RosterLine {
  group: RosterGroup;
  text: string;
  tone: RosterTone;
}

/**
 * V22 groupStats: the roster line for one attendee from the note's status and what is left.
 *
 * ehr#688 (GROUP_NOTE_10-29-V25 groupStats): a signed note says when ("Signed 3:04 PM", "Co-signed
 * 3:10 PM"; Co-signed only once the supervisor has), a note sent back to the reviewer again says
 * which round ("Resubmitted, round 2"), and a returned note says how many of the reviewer's
 * comments are still open ("Changes requested, 2 open"). Each extra is optional: without it the
 * line reads as before.
 */
export function rosterLine(n: {
  /** The note's status; null when there is no note yet. */
  status: string | null;
  left: readonly string[];
  absent: boolean;
  /** "Did not attend" on a group, "Not present" on a family session. */
  absentLabel: string;
  /** ehr#688: when the note was signed (signed) or co-signed (cosigned), already formatted. */
  time?: string;
  /** ehr#688: the note's review round (1 on the first submit, +1 on every resubmit). */
  round?: number;
  /** ehr#688: the reviewer's comments still on a returned note. */
  openComments?: number;
}): RosterLine {
  const at = n.time ? ` ${n.time}` : '';
  // ehr#689: with the reviewer is its own group (Pending review), not Signed
  if (n.status === 'pending')
    return {
      group: 'pending',
      text: (n.round ?? 0) > 1 ? `Resubmitted, round ${n.round}` : 'Pending review',
      tone: 'done',
    };
  if (n.status === 'signed') return { group: 'signed', text: `Signed${at}`, tone: 'done' };
  if (n.status === 'cosigned') return { group: 'signed', text: `Co-signed${at}`, tone: 'done' };
  if (n.status === null) return { group: 'needs', text: 'Not started', tone: 'warn' };
  const open = n.status === 'changes' ? (n.openComments ?? 0) : 0;
  const head = open ? `Changes requested, ${open} open` : '';
  if (!n.left.length)
    return {
      group: 'ready',
      text: head || (n.absent ? `${n.absentLabel}. Ready to sign` : 'Ready to sign'),
      tone: 'ok',
    };
  const first = n.left[0]!;
  const text = n.left.length === 1 ? first : `${first}, +${n.left.length - 1} more`;
  return {
    group: 'needs',
    text: head
      ? `${head} · ${text}`
      : n.status === 'changes'
        ? `Changes requested · ${text}`
        : text,
    tone: 'warn',
  };
}

/** "Needs you, 3": the roster group's heading with its count (V22). */
export const groupHeading = (label: string, n: number) => `${label}, ${n}`;

/**
 * ehr#689 (COUPLES_AND_FAMILY_NOTES_10-29-V15 / GROUP_NOTE_10-29-V25 rosterHTML): the roster's groups,
 * top to bottom. Pending review sits between Ready to sign and Signed: a submitted note stays there
 * until the supervisor co-signs it.
 */
export const ROSTER_ORDER: readonly RosterGroup[] = ['needs', 'ready', 'pending', 'signed'];
export const ROSTER_GROUP_LABEL: Record<RosterGroup, string> = {
  needs: 'Needs you',
  ready: 'Ready to sign',
  pending: 'Pending review',
  signed: 'Signed',
};
/** The roster's headings in order ("Needs you, 1", "Pending review, 3"), empty groups left out. */
export function rosterHeadings(lines: readonly Pick<RosterLine, 'group'>[]): string[] {
  return ROSTER_ORDER.flatMap((g) => {
    const n = lines.filter((l) => l.group === g).length;
    return n ? [groupHeading(ROSTER_GROUP_LABEL[g], n)] : [];
  });
}

/**
 * ehr#571 round 2 (V22 openSignAll): how many hours after the session a saved note would be signed,
 * when that is past the practice's late-entry hours; null when it is on time (or has no times).
 */
export function lateHoursOf(b: Body, date: string, limit: number, now: Date): number | null {
  const s = (b.session ?? {}) as { time?: unknown; end?: unknown };
  const end = sessionEndAt(date, str(s.end), str(s.time));
  if (!end) return null;
  const h = hoursAfterSession(end, now);
  return isLateEntry(h, limit) ? h : null;
}

/**
 * ehr#689 F4 (COUPLES_AND_FAMILY_NOTES_10-29-V15 openCosignAll): Co-sign all ready notes warns once
 * while a review comment is still open on one of the notes it would co-sign ("1 comment is still
 * open on Daniel's note."); null when there is none, so it goes straight to the confirm.
 */
export function cosignOpenWarning(notes: readonly { name: string; open: number }[]): string | null {
  const on = notes.filter((n) => n.open > 0);
  const n = on.reduce((t, x) => t + x.open, 0);
  if (!n) return null;
  const who = on.map((x) => `${x.name}’s`);
  const where =
    who.length === 1 ? who[0] : `${who.slice(0, -1).join(', ')} and ${who[who.length - 1]}`;
  return `${n} comment${n === 1 ? ' is' : 's are'} still open on ${where} note${on.length === 1 ? '' : 's'}.`;
}

/**
 * ehr#689 F4: what Co-sign all did with each note, one line each in the roster's order
 * ("Daniel Whitfield: Co-signed", "Leo Whitfield: Skipped: Not sent for review yet.").
 */
export function cosignResultLines(
  r: {
    cosigned: readonly { contactId: string }[];
    skipped: readonly { contactId: string; reason: string }[];
  },
  nameOf: (contactId: string) => string,
  order: readonly string[] = [],
): string[] {
  const at = (c: string) => {
    const i = order.indexOf(c);
    return i < 0 ? order.length : i;
  };
  return [
    ...r.cosigned.map((c) => ({ c: c.contactId, t: 'Co-signed' })),
    ...r.skipped.map((x) => ({ c: x.contactId, t: `Skipped: ${x.reason}` })),
  ]
    .map((x, i) => ({ ...x, i }))
    .sort((x, y) => at(x.c) - at(y.c) || x.i - y.i)
    .map((x) => `${nameOf(x.c)}: ${x.t}`);
}
